import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACTIVITY_BUCKET_DEFS,
  ACTIVITY_STATUS_DEFS,
  ACTIVITY_STATUS_MAP,
  ACTIVITY_TYPE_DEFS,
  ACTIVITY_TYPE_MAP,
  PRIORITY_MAP,
  type ActivityBucket,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { useListState } from '../../lib/hooks';
import { date, relative } from '../../lib/format';
import { Icon } from '../../components/Icon';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Pager,
  SearchBox,
  TableSkeleton,
} from '../../components/ui';
import { InsightCards, type InsightItem } from '../../components/Insights';
import {
  CardPickerButton,
  applyChoice,
  useCardChoice,
  type CardChoice,
} from '../../components/CardPicker';
import {
  ColumnPickerButton,
  useColumnChoice,
  useColumnDrag,
  type ColumnDef,
} from '../../components/ColumnPicker';
import {
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import { useDebounced } from '../../lib/hooks';
import { Avatar, OwnerChip, useTeamDirectory } from '../../components/People';
import { useToast } from '../../components/Toast';
import { QuickCallModal } from '../../components/QuickCall';
import { MailCompose } from '../../components/MailCompose';
import { ActivityDetail } from './ActivityDetail';
import { NewActivityModal } from './NewActivityModal';
import { TaskDrawer } from '../agency/TaskDrawer';
import {
  LayoutColumn,
  ProfileLayoutBar,
  ProfileLayoutButton,
  ProfileLayoutProvider,
  Section,
  useProfileLayout,
  type ProfileLayoutApi,
  type SectionDef,
} from '../../components/ProfileLayout';

/**
 * أيقونة كل دلو.
 *
 * موضعها هنا لا في الكتالوج المشترك: أسماء الأيقونات تخصّ مجموعة أيقونات
 * هذه الواجهة، ووضعها في حزمة تستعملها الخدمات يربطها بما لا يعنيها.
 */
const BUCKET_ICONS: Record<ActivityBucket, 'alert' | 'clock' | 'calendar' | 'check'> = {
  overdue: 'alert',
  today: 'clock',
  upcoming: 'calendar',
  done: 'check',
};

interface ActivityRow {
  id: string;
  type: keyof typeof ACTIVITY_TYPE_MAP;
  status: keyof typeof ACTIVITY_STATUS_MAP;
  priority: keyof typeof PRIORITY_MAP;
  assigneeId: string | null;
  subject: string;
  dueAt: string | null;
  completedAt: string | null;
  /* ما يلزم لتنفيذ العمل من الصفّ: رقمٌ يُطلب وبريدٌ يُكتب إليه ورابطٌ يُدخل منه. */
  meetingUrl?: string | null;
  meetingProvider?: string | null;
  mapsUrl?: string | null;
  messageChannel?: string | null;
  mailTo?: string | null;
  callDirection?: string | null;
  account: { id: string; name: string; phone?: string | null; email?: string | null } | null;
  lead: { id: string; name: string; phone?: string | null; email?: string | null } | null;
  contact?: { id: string; name: string; phone?: string | null; email?: string | null } | null;
  deal: { id: string; title: string } | null;
  /** مهمة المشروع المزروعة من هذا النشاط — النقر يفتح لوحها الجانبي. */
  agencyTaskId?: string | null;
}

interface ActivityPage {
  items: ActivityRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/** كتالوج أعمدة الأنشطة. */
const ACTIVITY_COLUMNS: readonly ColumnDef[] = [
  { key: 'subject', label: 'الموضوع', fixed: true },
  { key: 'type', label: 'النوع' },
  { key: 'related', label: 'مرتبط بـ' },
  { key: 'owner', label: 'المسؤول' },
  { key: 'status', label: 'الحالة' },
  { key: 'due', label: 'الموعد' },
];

/** أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في activities.service.ts. */
const ACTIVITY_FILTERS: FilterDef[] = [
  { key: 'subject', label: 'الموضوع', kind: 'text' },
  {
    key: 'type',
    label: 'النوع',
    kind: 'enum',
    options: ACTIVITY_TYPE_DEFS.map((t) => ({ value: t.key, label: t.label })),
  },
  { key: 'owner', label: 'المسؤول', kind: 'user', allowNone: true },
  {
    key: 'status',
    label: 'الحالة',
    kind: 'enum',
    options: ACTIVITY_STATUS_DEFS.map((s) => ({ value: s.key, label: s.label })),
  },
  { key: 'due', label: 'الموعد', kind: 'date' },
];

/** لوحة الحِمل كما يردّها الخادم. */
interface DashboardView {
  byOwner: Array<{ assigneeId: string | null; planned: number; overdue: number; today: number }>;
  byStatus: Record<string, number>;
  unscheduled: number;
}

export function ActivitiesList({
  mineOnly = false,
  fixedType,
}: {
  mineOnly?: boolean;
  /** يقصر الشاشة على نوع واحد، فتصلح كشاشة "المكالمات" مستقلة. */
  fixedType?: 'TASK' | 'MEETING' | 'CALL' | 'MAIL';
}) {
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /*
   * الشاشة صارت تُنفَّذ منها لا تُقرأ فقط: المكالمة تُطلب، والاجتماع
   * يُدخل، والرسالة تُرسَل — كلٌّ بأداته الراسية في مكانها.
   */
  const [calling, setCalling] = useState<ActivityRow | null>(null);
  const [mailing, setMailing] = useState<ActivityRow | null>(null);
  const toast = useToast();
  const { assignable } = useTeamDirectory();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);

  const query = {
    ...state,
    q: debounced || undefined,
    page,
    ...(mineOnly ? { status: state.status ?? 'PLANNED' } : {}),
    ...(fixedType ? { type: fixedType } : {}),
  };

  const list = useQuery({
    queryKey: ['activities', query],
    queryFn: () => api<ActivityPage>(`/crm/activities${qs(query)}`),
  });

  /*
   * أعداد الدلاء تتبع تصفية الشاشة لا الشاشة كلها.
   *
   * `status` مستبعد منها عمدًا: الدلو يحمل حالته في تعريفه — «فات موعدها»
   * مجدولٌ بالضرورة و«أُنجزت» مكتملٌ بالضرورة — فتمرير حالة فوقه يُنتج
   * تقاطعًا فارغًا. و`bucket` مستبعد كذلك، وإلا عدّت كل بطاقة نفسها وحدها.
   */
  const {
    status: _status,
    bucket: _bucket,
    page: _page,
    type: _type,
    overdueOnly: _overdue,
    ...statsQuery
  } = query as Record<string, unknown>;

  /* شاشة نوعٍ واحد (المكالمات) تُبقي بطاقاتها ضمن نوعها؛ الشاشة العامة لا. */
  const statsScoped = { ...statsQuery, ...(fixedType ? { type: fixedType } : {}) };

  const stats = useQuery({
    queryKey: ['activities', 'stats', statsScoped],
    queryFn: () => api<Record<string, number>>(`/crm/activities/stats${qs(statsScoped)}`),
  });

  /* لوحة الأنشطة: على من الحِمل، وكم بلا موعد — ما لا تقوله البطاقات. */
  const board = useQuery({
    queryKey: ['activities', 'dashboard', statsScoped],
    queryFn: () => api<DashboardView>(`/crm/activities/dashboard${qs(statsScoped)}`),
  });

  const complete = useMutation({
    mutationFn: (id: string) => api(`/crm/activities/${id}/complete`, { method: 'POST', body: {} }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['activities'] });
      void queryClient.invalidateQueries({ queryKey: ['timeline'] });
      /* مفتاح الإحصاء يبدأ بـ'activities' كذلك، فالإبطال أعلاه يشمله. */
      toast('أُنجز النشاط');
    },
  });

  const allCards = activityInsights(stats.data ?? {}, fixedType);
  const { chosen, setChosen, reset: resetCards } = useCardChoice(
    'goyone.cards.activities',
    DEFAULT_ACTIVITY_CARDS,
  );
  const cardChoices: CardChoice[] = allCards.map(({ key, label }) => ({
    key,
    label,
    group: BUCKET_KEYS.has(key) ? 'الوقت' : key === 'total' ? 'العدد' : 'حسب النوع',
  }));

  const {
    visible: shownColumns,
    state: columnState,
    setState: setColumnState,
    reset: resetColumns,
    touched: columnsTouched,
  } = useColumnChoice('goyone.columns.activities', ACTIVITY_COLUMNS);
  const colDrag = useColumnDrag(columnState, setColumnState);
  /*
   * نمطُ لوحةِ الحِمل مرفوعٌ إلى الصفحة.
   *
   * زرُّ «تغيير النمط» يسكن في شريطِ الأدواتِ مع «الأعمدة» و«البطاقات»، فيلزم
   * أن تُشتقّ حالتُه هنا لا في اللوحة، وتُمرَّر إلى اللوحةِ وإلى زرِّها معًا.
   */
  const boardLayout = useProfileLayout('goyone.layout.activities-board', BOARD_SECTIONS);

  /*
   * رأسُ الجدولِ يلتصق تحت شريطِ الأدواتِ لا خلفَه.
   *
   * الشريطُ لاصقٌ بأعلى الصفحةِ، ورأسُ الجدولِ لاصقٌ كذلك — فإن لم يعرف
   * الرأسُ ارتفاعَ الشريطِ التصق عند صفرٍ فاختفى خلفه. فيُقاس ارتفاعُ
   * الشريطِ (وقد يتغيّر بالطيّ عند ضيق الشاشة) ويُمرَّر إلى CSS متغيّرًا،
   * فيلتصق الرأسُ تحته تمامًا مهما تبدّل.
   */
  const pageRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const bar = barRef.current;
    const page = pageRef.current;
    if (!bar || !page) return;
    const sync = () => page.style.setProperty('--act-bar-h', `${bar.offsetHeight}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(bar);
    return () => ro.disconnect();
  }, []);

  const now = Date.now();
  /* أثمّة تصفيةٌ قائمة؟ — بها يُفسَّر الفراغ حين لا نتيجة. */
  const filtered = Boolean(
    state.type || state.bucket || state.status || state.q || state.assigneeId || state.f,
  );

  return (
    <div className="page" ref={pageRef}>
      <PageHead
        title={
          mineOnly ? 'أعمالي' : fixedType ? ACTIVITY_TYPE_MAP[fixedType].plural : 'الأنشطة'
        }
        subtitle={
          mineOnly
            ? 'المهام والمواعيد المسندة إليك'
            : fixedType
              ? `سجل ${ACTIVITY_TYPE_MAP[fixedType].plural} مع العملاء`
              : 'المهام والاجتماعات والمكالمات والرسائل'
        }
      />

      {/*
        البطاقات تُصفّي بنقرة، والاختيار للمستخدم.
        دلوٌ يُنقر (فات موعدها) يضبط الوقت، ونوعٌ يُنقر (مكالمات) يضبط النوع،
        و«كل الأنشطة» يمسحهما. والمرشّحات المنسدلة أسفلها تحرّك الحالةَ نفسها،
        فما تختاره من هنا يظهر هناك.
      */}
      <InsightCards
        items={applyChoice(allCards, chosen)}
        active={state.bucket ?? state.type}
        onPick={(next) => {
          if (next === undefined || next === 'total') {
            patch({ bucket: undefined, type: undefined });
          } else if (BUCKET_KEYS.has(next)) {
            patch({ bucket: next, type: undefined });
          } else {
            patch({ type: next, bucket: undefined });
          }
        }}
        pending={stats.isPending}
        /* البطاقات لا تتقاسم مجموعًا واحدًا (الإجمالي والأنواع تتداخل). */
        showShare={false}
      />

      {/*
        لوحة الحِمل — تجيب عمّا لا تجيب عنه البطاقات: على مَن يقع العمل،
        وكم منه بلا موعد. وكل صفٍّ ينقر فيُصفّى الجدول على صاحبه، فالقراءة
        تنتهي بفعلٍ لا بتأمّل. وتُطوى في شاشة «أعمالي» — من يقرأ حِمله
        وحده لا يحتاج توزيع الفريق.
      */}
      {!mineOnly && (
        <ActivityBoard
          board={board.data}
          loading={board.isPending}
          state={state}
          patch={patch}
          layout={boardLayout}
        />
      )}

      <div className="toolbar sticky-bar" ref={barRef}>
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث في موضوع النشاط"
        />

        {!fixedType && (
          <select
            className="select"
            style={{ width: 'auto' }}
            value={state.type ?? ''}
            onChange={(e) => patch({ type: e.target.value || undefined, bucket: undefined })}
            aria-label="النوع"
          >
            <option value="">كل الأنواع</option>
            {ACTIVITY_TYPE_DEFS.map((t) => (
              <option key={t.key} value={t.key}>
                {t.plural}
              </option>
            ))}
          </select>
        )}

        <select
          className="select"
          style={{ width: 'auto' }}
          value={state.bucket ?? ''}
          onChange={(e) => patch({ bucket: e.target.value || undefined, type: undefined })}
          aria-label="الوقت"
        >
          <option value="">كل الأوقات</option>
          {ACTIVITY_BUCKET_DEFS.map((d) => (
            <option key={d.key} value={d.key}>
              {d.label}
            </option>
          ))}
        </select>

        <select
          className="select"
          style={{ width: 'auto' }}
          value={state.status ?? ''}
          onChange={(e) => patch({ status: e.target.value || undefined })}
          aria-label="الحالة"
        >
          <option value="">كل الحالات</option>
          <option value="PLANNED">مجدول</option>
          <option value="DONE">مكتمل</option>
          <option value="CANCELLED">ملغي</option>
        </select>

        {/* المسؤول مرشّحًا: «أنشطة من؟» سؤالٌ يسبق «أيّ نوع؟» في المتابعة. */}
        {!mineOnly && (
          <select
            className="select"
            style={{ width: 'auto' }}
            value={state.assigneeId ?? ''}
            onChange={(e) => patch({ assigneeId: e.target.value || undefined })}
            aria-label="المسؤول"
          >
            <option value="">كل المسؤولين</option>
            {assignable.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        )}

        {(state.type ||
          state.bucket ||
          state.status ||
          state.q ||
          state.assigneeId ||
          state.f) && (
          <button
            className="btn btn-sm"
            onClick={() => {
              setSearch('');
              patch({
                type: undefined,
                bucket: undefined,
                status: undefined,
                q: undefined,
                assigneeId: undefined,
                f: undefined,
              });
            }}
          >
            <Icon name="x" size={14} />
            إلغاء التصفية
          </button>
        )}

        {/* الإضافة والاختيار في الشريط اللاصق — في متناول من نزل آخر الصفحة. */}
        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          {/* «تغيير النمط» نزل هنا مع إخوته — يسحب قسمي اللوحة ويخفي أحدهما. */}
          {!mineOnly && <ProfileLayoutButton api={boardLayout} />}
          <FilterButton defs={ACTIVITY_FILTERS} filters={filters} onChange={setFilters} />
          <ColumnPickerButton
            columns={ACTIVITY_COLUMNS}
            state={columnState}
            onChange={setColumnState}
            onReset={resetColumns}
            touched={columnsTouched}
          />
          <CardPickerButton
            choices={cardChoices}
            chosen={chosen}
            onChange={setChosen}
            onReset={resetCards}
          />
          <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
            <Icon name="plus" size={14} />
            نشاط جديد
          </button>
        </span>
      </div>

      <FilterChips defs={ACTIVITY_FILTERS} filters={filters} onChange={setFilters} />

      <FilterScope
        defs={ACTIVITY_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/crm/activities/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head sticky-head-flow">
          <table className="table">
            <thead>
              <tr>
                {shownColumns.map((c) => (
                  <th key={c.key} {...colDrag.thProps(c)}>
                    {c.label}
                    <HeaderFilter colKey={c.key} />
                  </th>
                ))}
                <th />
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={shownColumns.length + 1} />}

            {list.data && (
              <tbody>
                {list.data.items.map((a) => {
                  const overdue =
                    a.status === 'PLANNED' && a.dueAt && new Date(a.dueAt).getTime() < now;
                  return (
                    <tr
                      key={a.id}
                      className="clickable"
                      /*
                        مهمةٌ مزروعةٌ في إدارة المشاريع تُفتح بلوحها الجانبي
                        نفسه — فتُنفَّذ وتُعلَّق وتُكمَل حيث تُدار سائر المهام.
                      */
                      onClick={() =>
                        a.type === 'TASK' && a.agencyTaskId
                          ? setOpenTaskId(a.agencyTaskId)
                          : setOpenId(a.id)
                      }
                    >
                      {shownColumns.map((col) => (
                        <ActivityCell
                          key={col.key}
                          column={col}
                          activity={a}
                          overdue={Boolean(overdue)}
                        />
                      ))}
                      {/*
                        عمود التنفيذ: أداةُ النوع أولًا ثم «إتمام» — والمنجَز
                        يُراجَع بزرِّ تفاصيله. الإجراءات لا تفتح الصفّ:
                        وجهتان مختلفتان في خليّةٍ واحدة.
                      */}
                      <td className="end" onClick={(e) => e.stopPropagation()}>
                        <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                          <RowAction
                            activity={a}
                            onCall={setCalling}
                            onMail={setMailing}
                            onOpen={setOpenId}
                            onOpenTask={setOpenTaskId}
                          />
                          {a.status === 'PLANNED' && (
                            <button
                              className="btn btn-sm"
                              onClick={() => complete.mutate(a.id)}
                              disabled={complete.isPending}
                              title="تعليم كمكتمل"
                            >
                              <Icon name="check" size={14} />
                              إتمام
                            </button>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {/*
          الفراغ بسببه: «لا نتيجة لهذه التصفية» غير «لا أنشطة بعد» — والأول
          يُعالَج بمسح المرشّحات لا بإنشاء نشاط.
        */}
        {list.data?.items.length === 0 &&
          (filtered ? (
            <EmptyState
              title="لا نشاط يطابق هذه التصفية"
              text="جرّب توسيعها — أو امسحها لترى كل الأنشطة."
              action={
                <button
                  className="btn btn-sm"
                  onClick={() => {
                    setSearch('');
                    patch({
                      type: undefined,
                      bucket: undefined,
                      status: undefined,
                      q: undefined,
                      assigneeId: undefined,
                      f: undefined,
                    });
                  }}
                >
                  <Icon name="x" size={14} />
                  امسح التصفية
                </button>
              }
            />
          ) : (
            <EmptyState
              title="لا توجد أنشطة"
              text="تُسجَّل الأنشطة من داخل ملف العميل أو الحساب فتبقى مرتبطة بسياقها — أو أنشئ واحدًا من هنا."
              action={
                <button className="btn btn-sm btn-primary" onClick={() => setCreating(true)}>
                  <Icon name="plus" size={14} />
                  نشاط جديد
                </button>
              }
            />
          ))}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
            onPageSize={(n) => patch({ pageSize: String(n), page: undefined })}
          />
        )}
      </div>
      </FilterScope>

      {openId &&<ActivityDetail id={openId} onClose={() => setOpenId(null)} />}

      {/* لوحة الاتصال راسيةً — المكالمة تبدأ وتُسجَّل وتُنهى من الجدول. */}
      {calling && (
        <QuickCallModal
          clientName={whoOf(calling).name}
          phone={whoOf(calling).phone!}
          link={{
            leadId: calling.lead?.id ?? null,
            accountId: calling.account?.id ?? null,
            contactId: calling.contact?.id ?? null,
          }}
          onClose={() => {
            setCalling(null);
            void queryClient.invalidateQueries({ queryKey: ['activities'] });
            void queryClient.invalidateQueries({ queryKey: ['timeline'] });
          }}
        />
      )}

      {/* الرسالة تُكتب وتُرسَل من صندوق الشركة وتُنسب إلى سجلّ صاحبها. */}
      {mailing && (
        <MailCompose
          to={mailing.mailTo ?? whoOf(mailing).email ?? ''}
          subject={mailing.subject}
          link={{
            leadId: mailing.lead?.id ?? undefined,
            accountRefId: mailing.account?.id ?? undefined,
            contactId: mailing.contact?.id ?? undefined,
          }}
          onClose={() => setMailing(null)}
          onSent={() => {
            /* أُرسلت فعلًا — فتُعلَّم منجَزةً بلا خطوةٍ ثانية. */
            complete.mutate(mailing.id);
            setMailing(null);
          }}
        />
      )}

      {openTaskId && (
        <TaskDrawer
          taskId={openTaskId}
          onClose={() => {
            setOpenTaskId(null);
            /* الإكمال في اللوح يزامن النشاط — فتُحدَّث القائمة والدلاء. */
            void queryClient.invalidateQueries({ queryKey: ['activities'] });
          }}
        />
      )}

      {creating && (
        <NewActivityModal
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['activities'] });
            void queryClient.invalidateQueries({ queryKey: ['timeline'] });
          }}
        />
      )}
    </div>
  );
}

/** خلية عمودٍ واحد — ترتيبها وإظهارها بيد المستخدم من منتقي الأعمدة. */
/* ==========================================================================
   لوحة الحِمل — على مَن يقع العمل، وما لا موعد له
   ========================================================================== */

/**
 * البطاقات تجيب عن «كم نشاطًا»، وهذه عن «على مَن».
 *
 * صفٌّ لكل موظّفٍ بشريطٍ يقيس حِمله المجدول، وفيه الفائتُ أحمرَ وما هو
 * اليوم كهرمانيًّا — فمن يوزّع العمل يرى المُثقَل والفارغ في نظرة. والنقر
 * يصفّي الجدول على صاحبه، فالقراءة تنتهي بفعل.
 *
 * و«بلا موعد» بطاقةٌ وحدها: نشاطٌ مجدولٌ لا يعرف متى لا يظهر في دلوٍ
 * زمنيّ — لا فائتًا ولا اليوم ولا قادمًا — فيُنسى بلا أن يشعر أحد.
 */
/** قسما لوحة الحِمل نمطًا — يُرتَّبان ويُخفَيان ويُكبَّران كسائر الأقسام. */
const BOARD_SECTIONS: SectionDef[] = [
  { key: 'load', title: 'حِمل الفريق', column: 'main' },
  { key: 'state', title: 'حال الجدول', column: 'side' },
];

function ActivityBoard({
  board,
  loading,
  state,
  patch,
  layout,
}: {
  board: DashboardView | undefined;
  loading: boolean;
  state: Record<string, string | undefined>;
  patch: (next: Record<string, string | undefined>) => void;
  /* نمطُ اللوحة يُرفع إلى الصفحة، فيسكن زرُّه في رأسها مع إخوته. */
  layout: ProfileLayoutApi;
}) {
  const { byId } = useTeamDirectory();

  if (loading) return <div className="skeleton mb-4" style={{ height: 120 }} />;
  if (!board) return null;

  const owners = board.byOwner.slice(0, 6);
  const peak = Math.max(1, ...owners.map((o) => o.planned));

  return (
    <ProfileLayoutProvider api={layout}>
      {/* شريطُ التحريرِ حين يُفتح — وزرُّه في شريطِ الأدواتِ مع إخوته. */}
      <ProfileLayoutBar api={layout} />
      <div className="two-col pl-two act-board mb-4">
        <LayoutColumn api={layout} column="main">
          <Section layoutKey="load">
      <div className="card is-board">
        <div className="card-head">
          <h2 className="card-title">حِمل الفريق</h2>
          <span className="tiny mute">المجدول على كل مسؤول — والنقر يصفّي الجدول</span>
        </div>
        <div className="card-body">
          {owners.length === 0 ? (
            <p className="mute small">لا أنشطة مجدولة.</p>
          ) : (
            <div className="act-load">
              {owners.map((o) => {
                const who = o.assigneeId ? byId.get(o.assigneeId) : null;
                const active = state.assigneeId === (o.assigneeId ?? '');
                return (
                  <button
                    key={o.assigneeId ?? 'none'}
                    type="button"
                    className={`act-load-row${active ? ' is-active' : ''}`}
                    onClick={() =>
                      patch({
                        assigneeId: active ? undefined : (o.assigneeId ?? undefined),
                        page: undefined,
                      })
                    }
                    title={who ? `أنشطة ${who.name}` : 'أنشطة بلا مسؤول'}
                  >
                    <Avatar name={who?.name ?? '؟'} src={who?.avatarUrl} size={26} />
                    <span className="act-load-who">{who?.name ?? 'بلا مسؤول'}</span>
                    <span className="act-load-bar" aria-hidden>
                      <span
                        className="act-load-fill"
                        style={{ width: `${Math.round((o.planned / peak) * 100)}%` }}
                      />
                    </span>
                    <span className="act-load-nums num">
                      {o.overdue > 0 && (
                        <span className="act-pill is-late" title="فات موعدها">
                          {o.overdue}
                        </span>
                      )}
                      {o.today > 0 && (
                        <span className="act-pill is-today" title="اليوم">
                          {o.today}
                        </span>
                      )}
                      <strong>{o.planned}</strong>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

          </Section>
        </LayoutColumn>

        <LayoutColumn api={layout} column="side">
          <Section layoutKey="state">
      <div className="card is-board">
        <div className="card-head">
          <h2 className="card-title">حال الجدول</h2>
        </div>
        <div className="card-body">
          <div className="act-state">
            {(
              [
                ['PLANNED', 'مجدولة', 'clock', 'var(--brand)'],
                ['DONE', 'مكتملة', 'check', 'var(--success)'],
                ['CANCELLED', 'ملغاة', 'x', 'var(--ink-4)'],
              ] as Array<[string, string, Parameters<typeof Icon>[0]['name'], string]>
            ).map(([key, label, icon, tone]) => (
              <button
                key={key}
                type="button"
                className={`act-state-row${state.status === key ? ' is-active' : ''}`}
                onClick={() =>
                  patch({ status: state.status === key ? undefined : key, page: undefined })
                }
              >
                <span className="act-state-ico" style={{ color: tone }}>
                  <Icon name={icon} size={14} />
                </span>
                {label}
                <strong className="num">{board.byStatus[key] ?? 0}</strong>
              </button>
            ))}
          </div>

          {/* ما لا موعد له — أخطرها لأنه لا يظهر في دلوٍ زمنيّ فيُنسى. */}
          <div className={`act-unsched${board.unscheduled > 0 ? ' is-warn' : ''}`}>
            <Icon name="alert" size={14} />
            <span>بلا موعد</span>
            <strong className="num">{board.unscheduled}</strong>
            {board.unscheduled > 0 && (
              <span className="tiny mute">لا تظهر في «اليوم» ولا «القادمة» — حدّد لها موعدًا.</span>
            )}
          </div>
        </div>
      </div>
          </Section>
        </LayoutColumn>
      </div>
    </ProfileLayoutProvider>
  );
}

/**
 * صاحب النشاط ووسائل بلوغه.
 *
 * السجلّ قد يكون عميلًا محتملًا أو عميلًا أو جهة اتصال — والأداة تحتاج
 * اسمًا ورقمًا وبريدًا أيًّا كان. وجهةُ الاتصال أولًا: هي المخاطَب حين
 * تُذكر، وإلا فالسجلّ الأمّ.
 */
function whoOf(a: ActivityRow): { name: string; phone: string | null; email: string | null } {
  const who = a.contact ?? a.lead ?? a.account;
  return {
    name: who?.name ?? 'العميل',
    phone: a.contact?.phone ?? a.lead?.phone ?? a.account?.phone ?? null,
    email: a.mailTo ?? a.contact?.email ?? a.lead?.email ?? a.account?.email ?? null,
  };
}

/**
 * أداةُ الصفّ — الشاشة تُنفَّذ منها لا تُقرأ فقط.
 *
 * كان الجدول يعرض ما ينبغي عمله ثم يترك عاملَه يبحث عن مكان عمله: يفتح
 * ملف العميل ليطلب رقمًا، أو ينسخ رابط اجتماعٍ من التفاصيل. الآن لكل
 * نوعٍ زرُّه في صفّه: المكالمة تُطلب بلوحةٍ راسية، والاجتماع يُدخل من
 * رابطه أو يُلاحَق إلى مكانه، والرسالة تُكتب وتُرسَل، والمهمة تُفتح في
 * لوحها. وما تعذّر لغياب رقمٍ أو رابط يُقال بصراحةٍ ولا يُعرض زرًّا كاذبًا.
 *
 * والمنجَز يُراجَع: زرُّ تفاصيلَ بلغة نوعه.
 */
function RowAction({
  activity: a,
  onCall,
  onMail,
  onOpen,
  onOpenTask,
}: {
  activity: ActivityRow;
  onCall: (a: ActivityRow) => void;
  onMail: (a: ActivityRow) => void;
  onOpen: (id: string) => void;
  onOpenTask: (taskId: string) => void;
}) {
  const who = whoOf(a);

  if (a.status !== 'PLANNED') {
    return (
      <button
        className={`btn btn-sm act-open ${ROW_TONE[a.type] ?? ''}`}
        onClick={() => onOpen(a.id)}
        title="راجع ما جرى"
      >
        <Icon name={ROW_ICON[a.type] ?? 'eye'} size={13} />
        {ROW_REVIEW[a.type] ?? 'التفاصيل'}
      </button>
    );
  }

  if (a.type === 'CALL') {
    return who.phone ? (
      <button className="btn btn-sm act-open is-call" onClick={() => onCall(a)}>
        <Icon name="phone" size={13} />
        اتصل
      </button>
    ) : (
      <span className="tiny mute">لا رقم مسجَّل</span>
    );
  }

  if (a.type === 'MEETING') {
    if (a.meetingUrl) {
      return (
        <a
          className="btn btn-sm act-open is-meeting"
          href={a.meetingUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon name="video" size={13} />
          ادخل الاجتماع
        </a>
      );
    }
    if (a.mapsUrl) {
      return (
        <a
          className="btn btn-sm act-nav"
          href={a.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon name="mapPin" size={13} />
          الملاحة إلى المكان
        </a>
      );
    }
    return <span className="tiny mute">حضوريّ بلا موقع</span>;
  }

  if (a.type === 'MAIL') {
    const channel = a.messageChannel ?? 'EMAIL';
    /*
     * البريد يُرسَل من صندوق الشركة هنا. أما قنوات ميتا وSMS فمُرسِلُها
     * في ملف صاحبها حيث محادثتُه — فيُقال ذلك ولا يُعرض زرٌّ لا يرسل.
     */
    if (channel !== 'EMAIL') {
      return <span className="tiny mute">تُرسَل من ملف العميل</span>;
    }
    return who.email ? (
      <button className="btn btn-sm act-open is-mail" onClick={() => onMail(a)}>
        <Icon name="send" size={13} />
        أرسل الآن
      </button>
    ) : (
      <span className="tiny mute">لا بريد مسجَّل</span>
    );
  }

  if (a.type === 'TASK' && a.agencyTaskId) {
    return (
      <button
        className="btn btn-sm act-open is-task"
        onClick={() => onOpenTask(a.agencyTaskId!)}
      >
        <Icon name="rocket" size={13} />
        افتح اللوح
      </button>
    );
  }

  return null;
}

/* لغةُ المراجعة وأيقونتُها ولمحتُها اللونية — لكل نوعٍ ما يخصّه. */
const ROW_REVIEW: Record<string, string> = {
  TASK: 'تفاصيل المهمة',
  MEETING: 'محضر الاجتماع',
  CALL: 'تفاصيل المكالمة',
  MAIL: 'نصّ الرسالة',
  NOTE: 'نصّ الملاحظة',
};
const ROW_ICON: Record<string, 'rocket' | 'video' | 'phone' | 'mail' | 'note'> = {
  TASK: 'rocket',
  MEETING: 'video',
  CALL: 'phone',
  MAIL: 'mail',
  NOTE: 'note',
};
const ROW_TONE: Record<string, string> = {
  TASK: 'is-task',
  MEETING: 'is-meeting',
  CALL: 'is-call',
  MAIL: 'is-mail',
  NOTE: 'is-note',
};
function ActivityCell({
  column,
  activity: a,
  overdue,
}: {
  column: ColumnDef;
  activity: ActivityRow;
  overdue: boolean;
}) {
  switch (column.key) {
    case 'subject':
      return <td className="cell-strong">{a.subject}</td>;
    case 'type':
      return <td className="cell-mute">{ACTIVITY_TYPE_MAP[a.type]?.label}</td>;
    case 'related':
      /* روابط السجل لا تفتح النشاط: وجهتان مختلفتان في خلية واحدة. */
      return (
        <td onClick={(e) => e.stopPropagation()}>
          {a.account ? (
            <Link to={`/crm/accounts/${a.account.id}`}>{a.account.name}</Link>
          ) : a.lead ? (
            <Link to={`/crm/leads/${a.lead.id}`}>{a.lead.name}</Link>
          ) : (
            '—'
          )}
        </td>
      );
    case 'owner':
      /* المسؤول بوجهه: «على من هذا؟» يُقرأ بنظرة لا بفتح النشاط. */
      return (
        <td>
          <OwnerChip ownerId={a.assigneeId} size={22} empty="بلا مسؤول" />
        </td>
      );
    case 'status':
      return (
        <td>
          <Badge kind={ACTIVITY_STATUS_MAP[a.status]?.kind}>
            {ACTIVITY_STATUS_MAP[a.status]?.label}
          </Badge>
        </td>
      );
    case 'due':
      return (
        <td className={overdue ? '' : 'cell-mute'}>
          {a.completedAt ? (
            relative(a.completedAt)
          ) : a.dueAt ? (
            <span style={{ color: overdue ? 'var(--danger)' : undefined }}>{date(a.dueAt)}</span>
          ) : (
            'بلا موعد'
          )}
        </td>
      );
    default:
      return <td>—</td>;
  }
}

/** مفاتيح دلاء الوقت — بها يُفرَّق ما يضبط `bucket` عمّا يضبط `type`. */
const BUCKET_KEYS = new Set<string>(ACTIVITY_BUCKET_DEFS.map((d) => d.key));

/** المعروض قبل أن يختار المستخدم — أكثر ما يُتابَع يوميًّا. */
const DEFAULT_ACTIVITY_CARDS = ['total', 'overdue', 'today', 'CALL'];

/**
 * بطاقات الأنشطة من أعداد الخادم — تفاعلية تُصفّي بنقرة.
 *
 * دلاء الوقت (فات موعدها، اليوم…) والأنواع (مكالمات، اجتماعات…) وإجماليّها،
 * كلُّها أعدادٌ قابلة للنقر. وشاشةُ نوعٍ واحد (المكالمات) تُسقط بطاقات الأنواع
 * والإجمالي: لا معنى لعدّ الاجتماعات في شاشة المكالمات.
 */
function activityInsights(s: Record<string, number>, fixedType?: string): InsightItem[] {
  const byType: InsightItem[] = [
    { key: 'CALL', label: 'مكالمات', icon: 'phone', color: '#5596FF' },
    { key: 'MEETING', label: 'اجتماعات', icon: 'video', color: '#51A521' },
    { key: 'TASK', label: 'مهام', icon: 'check', color: '#8791A1' },
    { key: 'MAIL', label: 'رسائل', icon: 'mail', color: '#6B7280' },
  ].map((c) => ({ ...c, count: s[c.key] ?? 0 }));

  const buckets: InsightItem[] = ACTIVITY_BUCKET_DEFS.map((d) => ({
    key: d.key,
    label: d.label,
    icon: BUCKET_ICONS[d.key],
    color: d.color,
    count: s[d.key] ?? 0,
    hint: d.hint,
  }));

  /* شاشة النوع الواحد: الدلاء وحدها تعني شيئًا. */
  if (fixedType) return buckets;

  return [
    { key: 'total', label: 'كل الأنشطة', icon: 'clipboard', color: '#3672EA', count: s.total ?? 0 },
    ...buckets,
    ...byType,
  ];
}

